import { useEffect, useState, useCallback, useRef } from "react";

/**
 * useChildSeats
 * 
 * Child seat allocations are managed independently from the vehicles state.
 * Allocations are stored by index (position), NOT by vehicleId.
 * This prevents child seat data from being lost when vehicle state changes
 * (e.g. vehicle type refetch, vehicle selection change, etc.).
 * 
 * The allocations are merged back into bookingInfo only when calling the
 * /edit-booking/simulation API (see buildSimulationPayload in useBookingSubmission).
 */
export function useChildSeats({ passengers }) {
    const [childSeatAllocations, setChildSeatAllocations] = useState([]);
    const vehicleCountRef = useRef(0);

    const getChildSeatLimit = useCallback((seatType) => {
        if (seatType === "infant") return Number(passengers?.baby || 0);
        if (seatType === "toddler") return Number(passengers?.toddler || 0);
        if (seatType === "child") return Number(passengers?.child || 0);
        return 0;
    }, [passengers]);

    const incrementChildSeat = useCallback((index, seatType) => {
        setChildSeatAllocations((prev) => {
            return prev.map((item, i) =>
                i === index
                    ? { ...item, [seatType]: Number(item?.[seatType] || 0) + 1 }
                    : item
            );
        });
    }, []);

    const decrementChildSeat = useCallback((index, seatType) => {
        setChildSeatAllocations((prev) => {
            return prev.map((item, i) => {
                if (i !== index) return item;
                const current = Number(item?.[seatType] || 0);
                return { ...item, [seatType]: Math.max(0, current - 1) };
            });
        });
    }, []);

    /**
     * Sync the number of allocation slots to match vehicle count.
     * Called from the parent when vehicles are added/removed.
     * Preserves existing allocations by index position.
     * Skips if allocations were already set (e.g. from booking detail load).
     */
    const syncAllocationsToVehicleCount = useCallback((vehicleCount) => {
        vehicleCountRef.current = vehicleCount;
        setChildSeatAllocations((prev) => {
            if (prev.length === vehicleCount) return prev;

            // If allocations already have data and the count matches or is being
            // set for the first time from booking detail, preserve them.
            if (vehicleCount > prev.length) {
                // Add new empty slots at the end
                const newSlots = Array.from(
                    { length: vehicleCount - prev.length },
                    () => ({ infant: 0, toddler: 0, child: 0 })
                );
                return [...prev, ...newSlots];
            }

            // Remove from the end
            return prev.slice(0, vehicleCount);
        });
    }, []);

    // Clamp allocations when passengers change
    useEffect(() => {
        const clampSeatType = (allocations, seatType, limit) => {
            if (limit <= 0) {
                return allocations.map((item) => ({ ...item, [seatType]: 0 }));
            }

            const total = allocations.reduce(
                (sum, item) => sum + Number(item?.[seatType] || 0),
                0
            );
            if (total <= limit) return allocations;

            let overflow = total - limit;
            const next = allocations.map((item) => ({ ...item }));

            for (let index = next.length - 1; index >= 0 && overflow > 0; index -= 1) {
                const current = Number(next[index]?.[seatType] || 0);
                const reduceBy = Math.min(current, overflow);
                next[index][seatType] = current - reduceBy;
                overflow -= reduceBy;
            }

            return next;
        };

        setChildSeatAllocations((prev) => {
            if (prev.length === 0) return prev;

            let next = prev.map((item) => ({ ...item }));
            next = clampSeatType(next, "infant", Number(passengers?.baby || 0));
            next = clampSeatType(next, "toddler", Number(passengers?.toddler || 0));
            next = clampSeatType(next, "child", Number(passengers?.child || 0));

            const hasChanged = next.some((item, index) => {
                const current = prev[index];
                if (!current) return true;
                return (
                    Number(item?.infant || 0) !== Number(current?.infant || 0) ||
                    Number(item?.toddler || 0) !== Number(current?.toddler || 0) ||
                    Number(item?.child || 0) !== Number(current?.child || 0)
                );
            });

            return hasChanged ? next : prev;
        });
    }, [passengers]);

    return {
        childSeatAllocations,
        setChildSeatAllocations,
        incrementChildSeat,
        decrementChildSeat,
        syncAllocationsToVehicleCount,
    };
}
